import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from "react-redux";
import { fetchDashboardStats, selectDashboardStats } from "slices/dashboard/dashboardSlice";
import { AppDispatch } from "slices/app/store/store";
import { MenuItem } from "types/types";
import { HasPermission } from "./Permissions/permissionsCache";
import { useLocation } from 'react-router-dom';

const filterByPermission = (items: MenuItem[]): MenuItem[] => {
  return items
    .filter((item) => !item.permission || HasPermission(item.permission))
    .map((item) => ({
      ...item,
      subItems: item.subItems ? filterByPermission(item.subItems) : undefined,
    }));
};

const useMenuData = () => {
  const dispatch = useDispatch<AppDispatch>();
  const location = useLocation();
  const stats = useSelector(selectDashboardStats);
  const [loadingStats, setLoadingStats] = useState(true);
  const [activeMenu, setActiveMenu] = useState("Dashboard");
  const [openMenus, setOpenMenus] = useState<Record<string, boolean>>({
    dashboard: false,
    inward: false,
    return: false,
    franchiesereturn: false,
    transaction: false,
    repair: false,
    retunStoreTransfer: false,
    report: false,
    user: false,
  });

  const isMainStoreEnabled = (localStorage.getItem('isMainStore') ?? 'false') === 'true';

  const loadStats = async () => {
    try {
      await dispatch(fetchDashboardStats());
    } catch (error) {
      console.error("Failed to load dashboard stats:", error);
    } finally {
      setLoadingStats(false);
    }
  };

  useEffect(() => {
    loadStats();
  }, [dispatch]);

  useEffect(() => {
    loadStats();
  }, [location.pathname]);

  const updateIconSidebar = useCallback((e: React.MouseEvent) => {
    if (e.currentTarget.getAttribute("data-sub-items")) {
      const ul = document.getElementById("two-column-menu");
      if (!ul) return;

      const iconItems = ul.querySelectorAll(".nav-icon.active");
      iconItems.forEach((item) => {
        item.classList.remove("active");
        const id = item.getAttribute("data-sub-items");
        if (id) {
          const menusId = document.getElementById(id);
          if (menusId && menusId.parentElement) {
            menusId.parentElement.classList.remove("show");
          }
        }
      });

      e.currentTarget.classList.add("active");
    }
  }, []);

  const handleMenuClick = useCallback((e: React.MouseEvent, menuId: string) => {
    e.preventDefault();
    setOpenMenus(prev => {
      const newState: Record<string, boolean> = {};
      Object.keys(prev).forEach(key => {
        newState[key] = false; // close all
      });
      newState[menuId] = !prev[menuId]; // toggle clicked one
      return newState;
    });
    setActiveMenu(menuId);
    updateIconSidebar(e);
  }, [updateIconSidebar]);


  const renderCountBadge = useCallback((count: number | undefined) => {
    return (
      <span className="badge bg-warning rounded-pill float-end">
        {count || 0}
      </span>
    );
  }, []);

  useEffect(() => {
    document.body.classList.remove("twocolumn-panel");

    setOpenMenus(prev => {
      const newState = { ...prev };
      Object.keys(newState).forEach(key => {
        if (key.toLowerCase() !== activeMenu.toLowerCase()) {
          newState[key] = false;
        }
      });
      return newState;
    });
  }, [activeMenu]);

  const menuItems = useMemo(() => {
    const Items: MenuItem[] = [
      {
        id: "dashboard",
        label: "Dashboard",
        icon: "bx bx-grid-alt",
        link: "/dashboard",
        click: (e) => handleMenuClick(e, "dashboard"),
        stateVariables: openMenus.dashboard,
      },
      {
        id: "inward",
        label: "Stock",
        icon: "bx bx-box",
        link: "/#",
        permission: "ProductHeader.Read",
        click: (e) => handleMenuClick(e, "inward"),
        stateVariables: openMenus.inward,
        subItems: [
          {
            id: "diamondInward",
            label: (
              <>
                Diamond Inward {renderCountBadge(stats?.diamondInward)}
              </>
            ),
            link: "/diamond-product-inward",
            permission: 'ProductHeader.Read'
          },
          {
            id: "diamondProductInventory",
            label: "Diamond Product Inventory",
            link: "/diamond-product-inventory",
            permission: 'ProductHeader.Read'
          },
          {
            id: "goldInward",
            label: (
              <>
                Gold Inward
                {renderCountBadge(stats?.goldInward)}
              </>
            ),
            link: "/gold-product-inward",
            permission: 'ProductHeader.Read'
          },
          {
            id: "goldProductInventory",
            label: "Gold Product Inventory",
            link: "/gold-product-inventory",
            permission: 'ProductHeader.Read'
          },
          {
            id: "SNMCCdiamondInward",
            label: (
              <>
                Memo Diamond Inward
                {renderCountBadge(stats?.memoDiamondInward)}
              </>
            ),
            link: "/SNMCC-diamond-product-inward",
            permission: 'ProductHeader.Read'
          },
          {
            id: "SNMCCdiamondProductInventory",
            label: "Memo Diamond Product Inventory",
            link: "/SNMCC-diamond-product-inventory",
            permission: 'ProductHeader.Read'
          },
          {
            id: "SNMCCgoldInward",
            label: (
              <>
                <span style={{ fontSize: 12 }}>Memo Gold Inward</span>
                {renderCountBadge(stats?.memoGoldInward)}
              </>
            ),
            link: "/SNMCC-gold-product-inward",
            permission: 'ProductHeader.Read'
          },
          {
            id: "SNMCCgoldProductInventory",
            label: "Memo Gold Product Inventory",
            link: "/SNMCC-gold-product-inventory",
            permission: "ProductHeader.Read"
          },
        ],
      },
      {
        id: "return",
        label: "Return To Brand",
        icon: "bx bx-refresh",
        link: "/#",
        permission: 'ReturnToMayaveProduct.Read',
        click: (e) => handleMenuClick(e, "return"),
        stateVariables: openMenus.return,
        subItems: [
          {
            id: "returnRequest",
            label: "Return Request",
            link: "/return-request",
          },
        ],
      },
      {
        id: "retuntofranchies",
        label: "Return Franchise",
        icon: "bx bx-transfer",
        permission: 'ReturnSalesOrder.Read',
        link: "/#",
        click: (e) => handleMenuClick(e, "franchiesereturn"),
        stateVariables: openMenus.franchiesereturn,
        subItems: [
          {
            permission: 'ReturnSalesOrder.Read',
            id: "return-to-franchise",
            label: "Return Customer To Franchise",
            link: "/return-to-franchise",
          },
          {
            id: "returnSales",
            label: "Return Sales",
            permission: 'ReturnSalesOrder.Read',
            link: "/return-sales",
          },
        ],
      },
      {
        id: "transaction",
        label: "Transaction",
        icon: "bx bx-rupee",
        link: "/#",
        permission: 'SalesEntry.Read',
        click: (e) => handleMenuClick(e, "transaction"),
        stateVariables: openMenus.transaction,
        subItems: [
          {
            id: "new-transaction-entry",
            label: "New Transaction Entry",
            link: "/new-transaction-entry",
            permission: 'SalesEntry.Write',
          },
          {
            id: "cart",
            label: "Draft Transaction Details",
            link: "/cart-details",
            permission: 'SalesEntry.Read',
          },
          {
            id: "sales-entry-list",
            label: "Sales Entry List",
            link: "/sales-entry-list",
            permission: 'SalesEntry.Read',
          },
          {
            permission: 'SalesEntry.Read',
            id: "sales-inventory",
            label: "Sales Inventory",
            link: "/sales-inventory",
          },
          {
            id: "old-gold-purchase-list",
            label: "OG Purchase List",
            link: "/old-gold-purchase-list",
            permission: 'PurchaseOrder.Read',
          },
          {
            permission: 'PurchaseOrder.Read',
            id: "bagging-list",
            label: "OG Purchase",
            link: "/bagging-list",
          },
          {
            permission: 'PurchaseOrder.Write',
            id: "old-gold-request",
            label: "OG Request",
            link: "/old-gold-request",
          },
          {
            id: "estimate-payment-receipt",
            label: "Make To Order",
            link: "/make-to-order",
            permission: 'EstimatePayment.Read'
          },
          {
            permission: 'EstimatePayment.Read',
            id: "custom-order-list",
            label: "Make To Order List",
            link: "/custom-order-list",
          },
        ],
      },
        {
        id: "dashboard",
        label: "E-com Darft Orders",
        icon: "bx bx-cart",
        link: "/ecom-draft-orders",
        click: (e) => handleMenuClick(e, "ecomDraftOrders"),
        stateVariables: openMenus.ecomDraftOrders,
      },
      {
        id: "Ledger",
        label: "Ledger",
        icon: "bx bx-book",
        permission: 'Ledger.Read',
        link: "/#",
        click: (e) => handleMenuClick(e, "franchiesereturn"),
        stateVariables: openMenus.franchiesereturn,
        subItems: [
          {
            id: "customer-ledger",
            label: "Customer Ledger",
            link: "/customer-ledger",
            permission: 'Ledger.Read'
          },
          {
            id: "storeLedger",
            label: "Store Ledger",
            permission: 'StoreLedger.Read',
            link: "/store-ledger",
          },
        ],
      },
      {
        id: "repair",
        label: "Repair",
        icon: "bx bx-cog",
        link: "/#",
        permission: 'RepairWork.Read',
        click: (e) => handleMenuClick(e, "repair"),
        stateVariables: openMenus.repair,
        subItems: [
          {
            id: "repair-product-search",
            label: "Repair Product Search",
            link: "/repair-product-search",
            permission: 'RepairWork.Write'
          },
          {
            id: "repair-product-list",
            label: "Repair Product List",
            link: "/repair-product-list",
            permission: 'RepairWork.Write'
          },
          {
            id: "repair-product-bag-list",
            label: "Repair Product Bag List",
            link: "/repair-product-bag-list",
            permission: 'RepairWork.Write'
          },
        ]
      },
      {
        id: "retunStoreTransfer",
        label: "Interstore",
        icon: "bx bxs-truck",
        permission: 'InterStore.Read',
        link: "/#",
        click: (e) => handleMenuClick(e, "retunStoreTransfer"),
        stateVariables: openMenus.retunStoreTransfer,
        subItems: [
          {
            id: "interStoreTransfer",
            label: "Inter Store Transfer",
            link: "/interStore-transfer",
            permission: 'InterStore.Write'
          },
          {
            id: "interStoreInventory",
            permission: 'InterStore.Read',
            label: "Inter Store Inventory",
            link: "/interStore-inventory",
          },
          {
            permission: 'InterStore.Write',
            id: "interStoreInventory",
            label: "Inter Store Inventory Request",
            link: "/interStore-Request",
          },
        ],
      },
      {
        id: "reward",
        label: "Reward",
        icon: "bx bx-gift",
        permission: 'Reward.Read',
        link: "/#",
        click: (e) => handleMenuClick(e, "reward"),
        stateVariables: openMenus.reward,
        subItems: [
          {
            id: "Referral",
            label: "Referral ",
            link: "/Customer-Referral",
            permission: 'Referral.Write',
          },
          {
            id: "RewardHistory",
            label: "Reward History",
            link: "/reward-History",
            permission: 'Reward.Write',
          },
          {
            id: "BirthdayAnniversary",
            label: "Birthday And Anniversary List",
            link: "/birthday-anniversary",
            permission: 'Reward.Write',
          },
        ]
      },
      {
        id: "report",
        label: "Report",
        icon: "bx bx-file-blank",
        link: "/report-page",
        click: (e) => handleMenuClick(e, "report"),
        stateVariables: openMenus.report,
      },
      {
        id: "user",
        label: "User",
        icon: "bx bx-user",
        link: "/#",
        permission: 'User.Read',
        click: (e) => handleMenuClick(e, "user"),
        stateVariables: openMenus.user,
        subItems: [
          {
            id: "user-master",
            label: "User",
            link: "/user-master",
            permission: "User.Read"
          },
          {
            id: "user-mster-role",
            label: "Role Master",
            link: "/role-master",
            permission: "UserRole.Read"
          },
        ],
      },
        {
        id: "dashboard",
        label: "Return Policy",
        icon: "bx bx-file",
        link: "/return-policy",
        click: (e) => handleMenuClick(e, "returnPolicy"),
        stateVariables: openMenus.returnPolicy,
      },
    ];

    return filterByPermission(Items);
  }, [stats, openMenus, isMainStoreEnabled, handleMenuClick, renderCountBadge]);

  return { menuItems, loadingStats, loadStats };
};

export default useMenuData